import { z } from "zod";

import { SiteDashboardProperties } from "~/api/Api";

import { DASHBOARD_NUM_COLUMNS } from "../constants";

export type WidgetConfig = SiteDashboardProperties["widgets"][number];

export const WidgetPlacementSchema = z.object({
  x: z
    .number()
    .int()
    .min(0)
    .max(DASHBOARD_NUM_COLUMNS - 1),
  y: z.number().int().min(0),
  w: z.number().int().min(1).max(DASHBOARD_NUM_COLUMNS),
  h: z.number().int().min(1),
});

export type WidgetPlacement = z.infer<typeof WidgetPlacementSchema>;

export const ViewTypeSchema = z.enum([
  "table",
  "bar-chart",
  "line-chart",
  "pie-chart",
  "cards",
]);

export type ViewType = z.infer<typeof ViewTypeSchema>;

export const WidgetMetadataSchema = z.object({
  placement: WidgetPlacementSchema,
  title: z.string(),
  viewType: ViewTypeSchema,
});

export type WidgetMetadata = z.infer<typeof WidgetMetadataSchema>;

// Default metadata values
const defaultMetadata: WidgetMetadata = {
  placement: {
    x: 0,
    y: 0,
    w: 1,
    h: 1,
  },
  title: "Unnamed widget",
  viewType: "table",
};

// Schema with default handling for missing or invalid metadata
export const WidgetMetadataWithDefaultSchema =
  WidgetMetadataSchema.default(defaultMetadata).catch(defaultMetadata);

export const DecodedWidgetConfigSchema = z.discriminatedUnion("method", [
  z.object({
    id: z.string(),
    integration: z.literal("google_analytics"),
    method: z.literal("property_report"),
    metadata: WidgetMetadataWithDefaultSchema,
    query: z.unknown(),
  }),
  z.object({
    id: z.string(),
    integration: z.literal("google_analytics"),
    method: z.literal("property_realtime_report"),
    metadata: WidgetMetadataWithDefaultSchema,
    query: z.unknown(),
  }),
  z.object({
    id: z.string(),
    integration: z.literal("google_search_console"),
    method: z.literal("search_analytics"),
    metadata: WidgetMetadataWithDefaultSchema,
    query: z.object({
      dimensions: z.array(z.string()).optional(),
      rowLimit: z.number().optional(),
    }),
  }),
]);

export type DecodedWidgetConfig = z.infer<typeof DecodedWidgetConfigSchema>;
